// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../config/firebase';
import toast from 'react-hot-toast';
import sessionManager from '../services/sessionManager';
import auditService from '../services/auditService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirebaseAvailable, setIsFirebaseAvailable] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  // Check if Firebase is available
  useEffect(() => {
    setIsFirebaseAvailable(!!auth && !!db);
  }, []);

  // Load user profile from Firestore
  const loadUserProfile = async (uid) => {
    if (!db || !uid) return null;

    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profile = userDoc.data();
        setUserProfile(profile);
        setUserRole(profile.role || 'staff');
        return profile;
      }
      return null;
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  };

  // Initialize session management callbacks
  useEffect(() => {
    sessionManager.setCallbacks({
      onIdle: handleIdleLogout,
      onWarning: () => setShowIdleWarning(true),
      onActivity: () => setShowIdleWarning(false)
    });
  }, []);

  // Handle idle logout
  const handleIdleLogout = async () => {
    try {
      // Log the idle logout before signing out
      if (currentUser && userProfile) {
        await auditService.logLogout(currentUser.uid, userProfile, 'session_timeout');
      }
      
      await signOut(auth);
      sessionManager.cleanup();
      auditService.clearSessionData();
      toast.error('You have been logged out due to inactivity');
    } catch (error) {
      console.error('Error during idle logout:', error);
    }
  };

  // Auth state listener
  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const profile = await loadUserProfile(user.uid);
        
        // Start session management for authenticated users
        if (profile) {
          const deviceType = sessionManager.getDeviceType(profile.role);
          sessionManager.setCurrentDeviceType(deviceType);
          sessionManager.startIdleTimeout(deviceType, profile.role);
          
          // Log successful authentication
          try {
            await auditService.logLogin(user.uid, profile);
            auditService.setLoginTime();
          } catch (error) {
            console.warn('Audit logging failed (non-critical):', error.message);
          }
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setUserRole(null);
        sessionManager.cleanup();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in function with role-based redirect and session management
  const signIn = async (email, password, navigate, options = {}) => {
    if (!auth) {
      throw new Error('Firebase Auth is not available');
    }

    const { rememberMe = false } = options;

    try {
      // Set persistence before signing in
      const deviceType = sessionManager.getDeviceType('staff'); // Default until we know the role
      await sessionManager.setPersistence(rememberMe, deviceType);
      
      const result = await signInWithEmailAndPassword(auth, email, password);
      const profile = await loadUserProfile(result.user.uid);
      
      // Update device type based on actual user role
      if (profile) {
        const actualDeviceType = sessionManager.getDeviceType(profile.role);
        sessionManager.setCurrentDeviceType(actualDeviceType);
        
        // Re-set persistence with correct device type
        await sessionManager.setPersistence(rememberMe, actualDeviceType);
        
        // Start idle timeout
        sessionManager.startIdleTimeout(actualDeviceType, profile.role);
      }
      
      // Role-based redirect after successful login
      if (navigate && profile) {
        const role = profile.role;
        if (role === 'admin') {
          toast.success('Welcome to your clinic dashboard!');
          navigate('/admin');
        } else if (role === 'super_admin') {
          toast.success('Welcome back, Super Admin!');
          navigate('/super-admin');
        } else {
          toast.success('Successfully signed in!');
          // Default users stay on current page or go to home
        }
      } else {
        toast.success('Successfully signed in!');
      }
      
      return result;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  // Sign out function with session cleanup
  const logout = async () => {
    if (!auth) return;

    try {
      // Log the logout before signing out
      if (currentUser && userProfile) {
        await auditService.logLogout(currentUser.uid, userProfile, 'user_initiated');
      }
      
      await signOut(auth);
      sessionManager.cleanup();
      auditService.clearSessionData();
      setCurrentUser(null);
      setUserProfile(null);
      setUserRole(null);
      setShowIdleWarning(false);
      toast.success('Successfully signed out!');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Error signing out');
    }
  };

  // Create user account (Admin only)
  const createUserAccount = async (userData, tenantId) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    if (!tenantId) {
      throw new Error('Tenant ID is required to create staff member.');
    }

    try {
      console.log('🔄 Attempting to call createStaffAccount Cloud Function...');
      
      // Use Cloud Function to create staff account with Firebase Auth + email
      const createStaffFunction = httpsCallable(functions, 'createStaffAccount');
      const result = await createStaffFunction({
        userData,
        tenantId
      });

      console.log('✅ Cloud Function succeeded:', result);
      toast.success(`Staff member ${userData.firstName} ${userData.lastName} created successfully! Password reset email sent.`);
      return result.data;
    } catch (error) {
      console.error('❌ Cloud Function failed:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // If Cloud Function fails, throw error immediately - don't fall back to Firestore-only
      if (error.code === 'functions/not-found') {
        throw new Error('Staff creation service is not deployed. Please contact administrator.');
      } else if (error.code === 'functions/unauthenticated') {
        throw new Error('You do not have permission to create staff accounts.');
      } else if (error.code === 'auth/email-already-in-use') {
        throw new Error(`Email ${userData.email} is already registered. Please use a different email address.`);
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Please use a stronger password.');
      } else {
        throw new Error(`Staff creation failed: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!db || !currentUser) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...updates,
        updatedAt: new Date().toISOString()
      });

      // Reload profile
      await loadUserProfile(currentUser.uid);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    if (!auth) {
      throw new Error('Firebase Auth is not available');
    }

    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  // Change password
  const changePassword = async (newPassword) => {
    if (!auth || !currentUser) {
      throw new Error('No authenticated user');
    }

    try {
      await updatePassword(currentUser, newPassword);

      // Update mustChangePassword flag
      if (userProfile?.mustChangePassword) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          mustChangePassword: false,
          passwordChangedAt: new Date().toISOString()
        });
        await loadUserProfile(currentUser.uid);
      }

      toast.success('Password updated successfully!');
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  // Record GDPR/PDPA consent
  const recordConsent = async (consentType, version = '1.0') => {
    if (!db || !currentUser) return;

    try {
      const consentData = {
        given: true,
        date: new Date().toISOString(),
        version: version,
        ipAddress: 'recorded', // In production, capture actual IP
        userAgent: navigator.userAgent
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        [`${consentType}Consent`]: consentData,
        updatedAt: new Date().toISOString()
      });

      await loadUserProfile(currentUser.uid);
      toast.success(`${consentType.toUpperCase()} consent recorded`);
    } catch (error) {
      console.error('Error recording consent:', error);
      throw error;
    }
  };

  // Check if user has required permissions
  const hasPermission = (requiredRole) => {
    if (!userRole) return false;

    const roleHierarchy = {
      'admin': 3,
      'clinician': 2,
      'staff': 1
    };

    const userLevel = roleHierarchy[userRole.toLowerCase()] || 0;
    const requiredLevel = roleHierarchy[requiredRole.toLowerCase()] || 0;

    return userLevel >= requiredLevel;
  };

  // Dismiss idle warning
  const dismissIdleWarning = () => {
    setShowIdleWarning(false);
    sessionManager.resetIdleTimer();
  };

  const value = {
    currentUser,
    userRole,
    userProfile,
    loading,
    isFirebaseAvailable,
    showIdleWarning,
    signIn,
    logout,
    createUserAccount,
    updateUserProfile,
    resetPassword,
    changePassword,
    recordConsent,
    hasPermission,
    loadUserProfile,
    dismissIdleWarning,
    sessionManager
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
